import React, { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { ColumnDef } from '@tanstack/react-table';

import { DataRow, useTableViewState } from '~components/table-view';
import EditableTableModal from '~support-at-home/components/common/EditableTableModal';

import { SahBudget } from '~support-at-home/models';

import columnsRaw from '~support-at-home/forms/sah-budgets/listSingle';

import { create, deleteOne, getOne, update } from '~support-at-home/actions/sahBudgets';

import { selectSahBudgetsByClient } from '~support-at-home/selectors/sahBudgets';

import { filterBudgetRowFields } from '../helpers';

const columns: ColumnDef<DataRow<SahBudget>>[] = columnsRaw as ColumnDef<DataRow<SahBudget>>[];

interface ComponentProps {
  id: number | null;
  isOpen?: boolean;
  onClose: (confirm: boolean) => void;
}

const BudgetsModal: React.FC<ComponentProps> = ({ id, isOpen = false, onClose }) => {
  const dispatch = useAppDispatch();

  const clientBudgets = useAppSelector(selectSahBudgetsByClient);
  const { data, setData } = useTableViewState<DataRow<SahBudget>>(clientBudgets as DataRow<SahBudget>[]);

  const load = async () => {
    dispatch(getOne({ client_id: id }));
  };

  useEffect(() => {
    if (isOpen && id !== null) {
      load();
    }
  }, [id, isOpen]);

  useEffect(() => {
    const persistedBudgets = clientBudgets.map((budget) => ({
      ...budget,
      persisted: true,
      backendId: budget.id,
    })) as DataRow<SahBudget>[];

    setData(persistedBudgets);
  }, [clientBudgets]);

  const getDefaultRow = () => {
    return {
      id: crypto.randomUUID(),
      client_id: id,
      sah_budget_id: '',
      origin_tag: 'Manual',
      start_date: '',
      end_date: '',
    } as DataRow<SahBudget>;
  };

  const onDependentChange = (row: DataRow<SahBudget>, columnId: string, _value: unknown): DataRow<SahBudget> => {
    if (['start_date', 'end_date', 'budget_amount', 'total_amount', 'used_amount'].includes(columnId)) {
      const total = row.total_amount ?? 0;
      const used = row.used_amount ?? 0;
      return {
        ...row,
        available_amount: total - used,
      };
    }
    return row;
  };

  return (
    <EditableTableModal
      isOpen={isOpen}
      title="Client SAH Budgets"
      data={data}
      columns={columns}
      onClose={(shouldReload) => {
        onClose(shouldReload);
        if (shouldReload) load();
      }}
      onCreate={async (row) => {
        const toCreate = filterBudgetRowFields(row, columns, id, row.sah_budget_id);
        await dispatch(create(toCreate)).unwrap();
        await load();
      }}
      onUpdate={async (row) => {
        if (!row.id) {
          return;
        }
        const toUpdate = filterBudgetRowFields(row, columns, id);
        await dispatch(update({ ...toUpdate, id: row.id })).unwrap();
        await load();
      }}
      onDelete={async (row) => {
        if (!row.backendId) {
          return;
        }
        try {
          await dispatch(deleteOne({ id: row.backendId })).unwrap();
          await load();
        } catch (error) {
          console.error('Error deleting budget:', error);
        }
      }}
      getDefaultRow={getDefaultRow}
      allowEdit={true}
      allowAdd={true}
      allowDelete={true}
      onDependentChange={onDependentChange}
    />
  );
};

export default BudgetsModal;
